'use client';

/**
 * DESIGN.md §7.7 — Navigation (Desktop).
 *
 * One navbar with two scroll-dependent modes. Mode 1 is the
 * traditional full-width transparent bar that sits over the hero;
 * Mode 2 is a centered pill on dark glass (jet/500 @ 64% +
 * backdrop-blur) that appears after the user scrolls past the top
 * of any (public) page. An IntersectionObserver on a one-pixel
 * sentinel placed BEFORE the over-hero header in document flow
 * drives the swap; the trigger is page-height-independent so the
 * pill appears reliably on short pages like /about too.
 *
 * The mobile/tablet variant (DESIGN.md §9.7) collapses the center
 * links into a hamburger that opens a full-width dropdown panel — the
 * panel is the §9.7 placeholder pattern (no Figma master yet).
 *
 * Server-renders Mode 1 (no scroll on first paint); the client
 * effect promotes to Mode 2 when applicable.
 */
import { useEffect, useRef, useState } from 'react';
import { NOUNS } from '../copy/marketing-copy.js';
import { cn } from '../lib/cn.js';
import { externalLinkProps } from '../lib/external-link.js';
import { Bounded } from '../primitives/bounded.js';
import { Icon } from '../primitives/icon.js';
import { Logo } from '../primitives/logo.js';
import { PillButton } from '../primitives/pill-button.js';
import { PlainTextLink } from '../primitives/plain-text-link.js';
import { TextLinkBtn1 } from '../primitives/text-link-btn-1.js';

interface NavLink {
  label: string;
  href: string;
}

/**
 * Spec 89 §2 — the dash0-style marketing header: a flat **Docs** slot
 * (takes the place Blog used to hold) plus a **Resources** dropdown that
 * collects the content-marketing pages (Blog + Guides). `link` renders a
 * plain text link; `menu` renders an accessible dropdown.
 */
type NavItem =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'menu'; label: string; items: readonly NavLink[] };

const NAV_ITEMS: readonly NavItem[] = [
  // Spec 115 phase B — the three product nouns under one "Product" menu.
  {
    kind: 'menu',
    label: 'Product',
    items: NOUNS.map((n) => ({ label: n.noun, href: `/${n.slug}` })),
  },
  { kind: 'link', label: 'Pricing', href: '/pricing' },
  { kind: 'link', label: 'Docs', href: '/docs' },
  {
    kind: 'menu',
    label: 'Resources',
    items: [
      { label: 'Blog', href: '/blog' },
      { label: 'Guides', href: '/guides' },
    ],
  },
  { kind: 'link', label: 'About', href: '/about' },
];

const SIGNIN_HREF = '/login';
const GET_STARTED_HREF = '/start';
const DASHBOARD_HREF = '/a';
const LOGOUT_HREF = '/logout';

export interface MarketingNavUser {
  displayName: string;
  avatarUrl?: string | null;
  /** Where the Dashboard button links to. Defaults to /a (the account picker). */
  href?: string;
}

export interface MarketingNavProps {
  /**
   * When supplied, the Sign in + Get Started CTA cluster is replaced
   * with a Dashboard pill button carrying the user's avatar on the
   * left. Resolve server-side from the session cookie in the
   * consuming layout and pass it down.
   */
  user?: MarketingNavUser | null;
  /**
   * Render with the mobile menu panel already open. For stories and
   * SSR smoke tests only — the app always starts closed.
   */
  defaultMobileOpen?: boolean;
}

// Avatar sizing: the Dashboard pill is a `size="md"` PillButton with
// `px-6 py-3 text-label-md` → ~44px tall when it contains text only.
// We want the avatar to fill that 44px outer height with a 1px
// transparent border (so the visible disc is 42px framed by 1px of
// pill color) WITHOUT pushing the pill taller. -my-3 cancels the
// pill's vertical padding for this child so a 44px avatar doesn't
// grow the parent (items-center keeps it visually centred). The pill
// also drops left padding via `pl-px` so the avatar sits 1px from the
// pill's left edge.
const NAV_AVATAR_RING = '-my-3 size-[44px] shrink-0 rounded-full border border-transparent';

export function NavAvatar({
  src,
  displayName,
  dark,
  className,
}: {
  src?: string | null;
  displayName: string;
  dark?: boolean;
  className?: string;
}) {
  if (src) {
    return (
      // Plain <img>: marketing-ui has no Next dependency, and the
      // avatar is already a small file fetched from the IdP — Next's
      // Image optimiser would not improve it.
      // biome-ignore lint/performance/noImgElement: see comment above
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className={cn(NAV_AVATAR_RING, 'object-cover', className)}
        width={44}
        height={44}
      />
    );
  }
  const initial = displayName.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden="true"
      className={cn(
        NAV_AVATAR_RING,
        'inline-flex items-center justify-center font-label text-label-md font-semibold',
        dark ? 'bg-primary-jet-300 text-primary-white' : 'bg-primary-jet-50 text-primary-jet-500',
        className,
      )}
    >
      {initial}
    </span>
  );
}

interface AuthCtaClusterProps {
  user?: MarketingNavUser | null;
  /** Floating-pill / mobile-menu context: text + fallback initial flip to light. */
  dark?: boolean;
  /** Mobile menu panel: stack the cluster full-width vertically. */
  stacked?: boolean;
}

export function LogoutIconButton({ dark, stacked }: { dark?: boolean; stacked?: boolean }) {
  // A 44px round icon button sized to match the Dashboard pill height, so
  // the cluster stays visually balanced. Contrast: on the light over-hero
  // bar, jet-500 border + icon on white (>7:1); on the dark pill / mobile
  // panel, white icon + steel border on jet glass. Hover fills the disc.
  return (
    <a
      href={LOGOUT_HREF}
      aria-label="Sign out"
      className={cn(
        'inline-flex size-[44px] shrink-0 items-center justify-center rounded-full border transition-colors',
        stacked && 'w-full',
        dark
          ? 'border-primary-steel-400/40 text-primary-white hover:bg-primary-white/10'
          : 'border-border-secondary text-icon-secondary hover:bg-surface-hover-1 hover:text-text-heading-1',
      )}
    >
      <Icon name="sign-out" size={20} title="Sign out" />
    </a>
  );
}

function AuthCtaCluster({ user, dark, stacked }: AuthCtaClusterProps) {
  if (user) {
    return (
      <>
        {/*
          Inline (nav bar): avatar and label sit side by side and the
          pair shrink-wraps, so `pl-px` pulls the avatar flush left.

          Stacked (mobile menu): the pill goes full-width, and a
          centered flex row would park the avatar next to the label in
          the middle of a very wide button. So the avatar comes OUT of
          flow — absolutely pinned to the left edge — leaving the label
          as the only flow child, which `justify-center` then centers
          against the button itself.

          Padding goes back to symmetric `px-6` in that mode: centering
          happens within the content box, so keeping `pl-px` against
          `pr-6` would bias the label ~12px left of true center. The
          absolute avatar ignores padding either way. `my-0` cancels
          NAV_AVATAR_RING's `-my-3` (a flow-centering trick) in favour
          of explicit vertical centering.
        */}
        <PillButton
          size="md"
          scheme="accent"
          href={user.href ?? DASHBOARD_HREF}
          fullWidth={stacked}
          className={cn('gap-2 pl-px', stacked && 'relative px-6')}
        >
          <NavAvatar
            src={user.avatarUrl}
            displayName={user.displayName}
            dark={dark}
            className={cn(stacked && 'absolute top-1/2 left-px my-0 -translate-y-1/2')}
          />
          <span>Dashboard</span>
        </PillButton>
        <LogoutIconButton dark={dark} stacked={stacked} />
      </>
    );
  }
  return (
    <>
      <TextLinkBtn1 href={SIGNIN_HREF} icon="sign-in" className={cn(dark && 'text-primary-white')}>
        Sign in
      </TextLinkBtn1>
      <PillButton size="md" scheme="accent" href={GET_STARTED_HREF} fullWidth={stacked}>
        Get Started
      </PillButton>
    </>
  );
}

/**
 * Desktop dropdown for a `menu` nav item. Opens on hover (marketing
 * convention) AND on click/keyboard (button + aria-expanded), closes on
 * Escape or outside click. The open panel lives inside an absolutely
 * positioned wrapper whose top padding bridges the visual gap to the
 * trigger, so moving the pointer into the menu never counts as a
 * mouse-leave. `dark` flips text/surface for the floating-pill context.
 */
function NavDropdown({ item, dark }: { item: Extract<NavItem, { kind: 'menu' }>; dark?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    // Hover open/close is a pointer-only enhancement; the <button> below
    // carries the real semantics (aria-haspopup/expanded + click + Escape),
    // so keyboard/AT users never depend on these handlers.
    // biome-ignore lint/a11y/noStaticElementInteractions: see comment above
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1 px-2 font-label font-medium text-label-md transition-colors',
          dark ? 'text-primary-white hover:text-primary-white/80' : 'text-text-on-action-2 hover:text-text-hover-1',
        )}
      >
        {item.label}
        <Icon name="chevron-down" size={20} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3">
          <div
            role="menu"
            aria-label={item.label}
            className={cn(
              'flex min-w-40 flex-col gap-1 rounded-xs border p-2 shadow-lg',
              dark
                ? 'border-primary-steel-400/40 bg-primary-jet-400'
                : 'border-border-primary bg-surface-page-secondary',
            )}
          >
            {item.items.map((sub) => (
              <a
                key={sub.href}
                href={sub.href}
                {...externalLinkProps(sub.href)}
                role="menuitem"
                className={cn(
                  'rounded-xl px-4 py-2 font-label font-medium text-label-md transition-colors',
                  dark
                    ? 'text-primary-white hover:bg-primary-white/10'
                    : 'text-text-on-action-2 hover:bg-surface-hover-1 hover:text-text-heading-1',
                )}
              >
                {sub.label}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The center nav row shared by both desktop headers (over-hero + pill). */
function DesktopNavItems({ dark }: { dark?: boolean }) {
  return (
    <>
      {NAV_ITEMS.map((item) =>
        item.kind === 'link' ? (
          <PlainTextLink
            key={item.href}
            href={item.href}
            className={cn('px-2 text-center', dark && 'text-primary-white')}
          >
            {item.label}
          </PlainTextLink>
        ) : (
          <NavDropdown key={item.label} item={item} dark={dark} />
        ),
      )}
    </>
  );
}

export function MarketingNav({ user, defaultMobileOpen = false }: MarketingNavProps = {}) {
  /**
   * `pillVisible` drives the opacity of the floating pill (true = 1,
   * false = 0); the over-hero header is the inverse. The signal
   * comes from an IntersectionObserver on a one-pixel sentinel
   * placed BEFORE the sticky over-hero header in the document flow.
   *
   * Why a sentinel + IO and not `window.scrollY`:
   *   - scrollY > 0 only fires if the scroll context is `window`.
   *     On routes where the page is shorter than the viewport (e.g.
   *     /about with a small MDX body), there's nothing to scroll
   *     and the pill never appeared.
   *   - IO fires the moment the sentinel leaves the viewport,
   *     regardless of scroll context, scroll-restoration state, or
   *     route height. If the page is too short to scroll, the
   *     sentinel never leaves the viewport and the over-hero stays
   *     visible — which is the correct outcome (over-hero IS the
   *     menu when no scroll is possible).
   * The CSS `transition-opacity duration-300` on each header keeps
   * the fade smooth instead of a hard snap.
   */
  const [pillVisible, setPillVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(defaultMobileOpen);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The sentinel div is part of every render, so it is mounted when this effect runs.
    const sentinel = sentinelRef.current as HTMLDivElement;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry) setPillVisible(!entry.isIntersecting);
      },
      // `threshold: 0` fires the moment the sentinel touches the
      // viewport edge. The default rootMargin/root is the viewport,
      // which is what we want.
      { threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Disable body scroll while the mobile menu is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Click-targets follow whichever header is the active one.
  const pillInteractive = pillVisible;
  const heroInteractive = !pillVisible;

  return (
    <>
      {/* Top-of-document sentinel. Sits in flow, height 1px, full
          width. While intersecting the viewport → over-hero shows /
          pill hidden; once it scrolls out → pill fades in. Routed
          before every (public) page so the IO trigger fires per
          page regardless of content height. */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />

      {/* Desktop nav (lg breakpoint) — two headers crossfade in place.
         The over-hero header is sticky-top and stays anchored; the
         floating pill is fixed-top and stays anchored. Only opacity
         changes on scroll, so neither slides into position.
         The hidden one gets pointer-events-none so its links stay
         inert behind the visible one. */}

      {/* §7.7 Mode 1 — over-hero header (full width, transparent) */}
      <header
        className={cn(
          'sticky top-0 z-30 hidden lg:block w-full pt-8 pb-6',
          'transition-opacity duration-300',
          pillVisible ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}
        aria-hidden={!heroInteractive}
      >
        {/* Content bounded to the 1312px column so the bar doesn't stretch
            edge-to-edge on wide screens; the (transparent) header stays
            full-width for sticky positioning. */}
        <Bounded className="flex items-center justify-between">
          <Logo variant="light" className="h-[30px]" />
          <nav className="flex items-center gap-10">
            <DesktopNavItems />
          </nav>
          <div className="flex items-center gap-2">
            <AuthCtaCluster user={user} />
          </div>
        </Bounded>
      </header>

      {/* §7.7 Mode 2 — floating pill (dark glass) */}
      {/* `top-3` (12px) instead of top-4 (16px) shifts the pill up
          by 4px so its inner text-line shares a baseline with the
          over-hero header's. Maths: over-hero content vertical-
          centre ≈ pt-8 + button_h/2 = 32 + 22 = 54; pill content
          centre = top + py-5 + button_h/2 = top + 42. top = 12 to
          land at 54. */}
      <header
        className={cn(
          'theme-dark fixed top-3 left-1/2 z-40 hidden lg:flex -translate-x-1/2',
          'w-[1312px] max-w-[calc(100%_-_128px)] items-center justify-between rounded-full',
          'bg-primary-jet-500/64 backdrop-blur-[57px] px-[30px] py-5',
          'transition-opacity duration-300',
          pillVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden={!pillInteractive}
      >
        <Logo variant="dark" className="h-[30px]" />
        <nav className="flex items-center gap-10">
          <DesktopNavItems dark />
        </nav>
        <div className="flex items-center gap-2">
          <AuthCtaCluster user={user} dark />
        </div>
      </header>

      {/* Tablet + Mobile nav — transparent like desktop so the hero
         art behind shows through. The open hamburger panel below
         brings its own surface-page-secondary background. */}
      {/* Scrolled state mirrors the desktop pill: once the sentinel
          leaves the viewport (pillVisible) the mobile bar swaps from
          transparent-over-hero to the same dark-glass treatment, so
          content never scrolls under an unbacked header. */}
      <header
        className={cn(
          'sticky top-0 z-30 block lg:hidden w-full transition-colors duration-300',
          pillVisible ? 'theme-dark bg-primary-jet-500/64 backdrop-blur-[57px] py-3' : 'pt-6 pb-4 md:pt-8 md:pb-6',
        )}
      >
        <Bounded className="flex items-center justify-between">
          <Logo variant={pillVisible ? 'dark' : 'light'} className="h-[24px] md:h-[30px]" />
          <div className="flex items-center gap-3 md:gap-6">
            <div className="hidden md:flex items-center gap-2">
              <AuthCtaCluster user={user} dark={pillVisible} />
            </div>
            <button
              type="button"
              className={cn(
                'flex size-10 items-center justify-center',
                pillVisible ? 'text-primary-white' : 'text-icon-secondary',
              )}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <Icon name={mobileOpen ? 'circle-x' : 'list'} size={mobileOpen ? 24 : 40} />
            </button>
          </div>
        </Bounded>
      </header>

      {/* Mobile / tablet menu panel — §9.7 placeholder pattern */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setMobileOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-primary-jet-500/40" aria-hidden="true" />
          <nav
            aria-label="Mobile primary"
            className="relative ml-auto mt-0 flex h-full w-full max-w-md flex-col gap-6 border-l border-border-primary bg-surface-page-secondary px-6 py-8 md:px-16"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {/* Panel chrome: the overlay covers the page header (z-50
                over z-30), so the panel brings its own logo + close
                button — without them there was no visible way out
                except tapping the dimmed backdrop. */}
            <div className="mb-2 flex items-center justify-between">
              <Logo variant="light" className="h-[24px]" />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="flex size-10 items-center justify-center text-icon-secondary transition-colors hover:text-text-heading-1"
              >
                <Icon name="circle-x" size={24} title="Close menu" />
              </button>
            </div>
            <PlainTextLink href="/" className="text-label-lg">
              Home
            </PlainTextLink>
            {NAV_ITEMS.map((item) =>
              item.kind === 'link' ? (
                <PlainTextLink key={item.href} href={item.href} className="text-label-lg">
                  {item.label}
                </PlainTextLink>
              ) : (
                <div key={item.label} className="flex flex-col gap-4">
                  <span className="font-label text-label-md font-semibold opacity-60">{item.label}</span>
                  {item.items.map((sub) => (
                    <PlainTextLink key={sub.href} href={sub.href} className="pl-3 text-label-lg">
                      {sub.label}
                    </PlainTextLink>
                  ))}
                </div>
              ),
            )}
            <div className="mt-auto flex flex-col gap-3 md:hidden">
              <AuthCtaCluster user={user} stacked />
            </div>
          </nav>
        </div>
      ) : null}
    </>
  );
}
